export enum PriceUnit {
  PER_DAY = 'PER_DAY',
  PER_HOUR = 'PER_HOUR',
  PER_SLOT = 'PER_SLOT',
}

export enum RecurrencePattern {
  SINGLE = 'single',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  SEASONAL = 'seasonal',
}

export enum ResourceType {
  SAUNA_BOOKING = 1,
  KEYS = 2,
  ANNUAL_INSPECTIONS = 3,
  PORT_WATCH_SHIFTS = 4,
}

export interface SeasonalDates {
  spring?: {
    start_date?: string;
    end_date?: string;
  };
  summer?: {
    start_date?: string;
    end_date?: string;
  };
  autumn?: {
    start_date?: string;
    end_date?: string;
  };
  winter?: {
    start_date?: string;
    end_date?: string;
  };
}

export interface InspectionSlot {
  startTime: string;
  endTime: string;
  inspectorId?: string;
  inspectorName?: string;
}

export interface DateWiseSlot {
  date: string; // YYYY-MM-DD format
  slots: InspectionSlot[];
}

export interface InspectionSlotGeneration {
  date: string;
  start_time: string;
  end_time: string;
}

// Sauna-specific interfaces
export interface SaunaTimeWindow {
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
}

export interface SaunaWeeklyWindows {
  mon?: SaunaTimeWindow[];
  tue?: SaunaTimeWindow[];
  wed?: SaunaTimeWindow[];
  thu?: SaunaTimeWindow[];
  fri?: SaunaTimeWindow[];
  sat?: SaunaTimeWindow[];
  sun?: SaunaTimeWindow[];
}

export interface SaunaSpecialDateWindow {
  date: string; // YYYY-MM-DD format
  windows: SaunaTimeWindow[];
}

export interface IClubResourceTranslation {
  description?: string;
}

export interface IClubResourceData {
  id?: string;
  club_id?: string;
  name: string;
  description?: string;
  resource_type?: ResourceType;
  allow_booking?: boolean;
  slot_length?: string;
  quantity?: number;
  recurrence_pattern?: RecurrencePattern;
  max_bookings_period_days?: number;
  define_price?: boolean;
  price?: number | null;
  price_unit?: PriceUnit | null;
  seasonal_dates?: SeasonalDates;
  // Generic date fields for non-PORT_WATCH_SHIFTS resource types
  start_date?: string;
  end_date?: string;
  // Port Watch Shifts specific fields
  shift_start_date?: string;
  shift_end_date?: string;
  shift_start_time?: string;
  shift_end_time?: string;
  port_watch_duty_liability?: number;
  port_watch_buyout_amount?: number | null;
  // Annual Inspection specific fields
  date_wise_slots?: DateWiseSlot[];
  slot_duration_minutes?: number;
  inspector_ids?: string[];
  inspection_slot_generations?: InspectionSlotGeneration[];
  // Sauna-specific fields
  weekly_windows?: SaunaWeeklyWindows;
  special_date_windows?: SaunaSpecialDateWindow[];
  closed_dates?: string[]; // Array of YYYY-MM-DD format dates
  status: number;
  created_at?: string;
  updated_at?: string;
  translations?: Record<string, IClubResourceTranslation>;
}

export interface IClubResourceResponse {
  data?: IClubResourceData;
}

export interface ClubResourceListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: number;
  club_id?: string;
}

export interface IPortWatchShift {
  id: string;
  club: {
    id: string;
    name: string;
    logo: string | null;
  };
  resource: {
    id: string;
    name: string;
    resource_type: number;
    quantity: number | null;
    slot_length: number | null;
    price: number | null;
    price_unit: string | null;
  };
  shift_start: string;
  shift_end: string;
  status: number;
  assigned_member: {
    id: string;
    name: string | null;
    email: string;
  } | null;
  assigned_at: string | null;
  completed_at: string | null;
}

export interface IPortWatchShiftsResponse {
  status: boolean;
  message: string;
  data: IPortWatchShift[];
}

export interface IAssignShiftPayload {
  user_id: string;
  override?: boolean;
}

export interface DutyLiabilityMember {
  member_id: string;
  user_id: string;
  name: string | null;
  email: string;
  required_duties: number;
  completed_duties: number;
  buyout_duties: number;
  fulfilled_duties: number;
  open_duties: number;
  buyout_amount_total: number;
  last_buyout_at: string | null;
  obligation_status:
    | 'NOT_APPLICABLE'
    | 'OPEN'
    | 'FULFILLED_BY_COMPLETED_DUTY'
    | 'FULFILLED_BY_BUYOUT'
    | 'FULFILLED_BY_MIXED';
  fulfilled: boolean;
}

export interface DutyLiabilitySummary {
  club_id: string;
  resource_id: string;
  period_start: string;
  period_end: string;
  required_duties: number;
  buyout_amount: number | null;
  members: DutyLiabilityMember[];
}

export interface DutyLiabilitySummaryResponse {
  status: boolean;
  message: string;
  data: DutyLiabilitySummary;
}
